import React, {
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from "react";

import { useGlobalContext } from "../Context/GlobalContext";

import { OlaMaps } from "olamaps-web-sdk";

/* ─── HELPERS ─── */
const isValidLatLng = (lat, lng) =>
  Number.isFinite(lat) && Number.isFinite(lng);

// =====================================================
// OLA MAP HELPERS
// =====================================================

const toOlaCoordinates = (positions = []) => {
  return positions
    .filter(
      ([lat, lng]) =>
        Number.isFinite(Number(lat)) && Number.isFinite(Number(lng)),
    )
    .map(([lat, lng]) => [
      Number(lng), // longitude first
      Number(lat), // latitude second
    ]);
};

const createOlaMarkerElement = (imageUrl, size = 40, rotation = 0) => {
  const element = document.createElement("div");

  element.style.width = `${size}px`;
  element.style.height = `${size}px`;
  element.style.display = "flex";
  element.style.alignItems = "center";
  element.style.justifyContent = "center";
  element.style.pointerEvents = "none";

  const image = document.createElement("img");

  image.src = imageUrl;
  image.draggable = false;

  image.style.width = "100%";
  image.style.height = "100%";
  image.style.objectFit = "contain";
  image.style.display = "block";
  image.style.transform = `rotate(${rotation}deg)`;
  image.style.transformOrigin = "center center";
  image.style.transition = "transform 0.3s ease-out";
  image.style.filter = "drop-shadow(0 4px 6px rgba(0,0,0,0.15))";

  element.appendChild(image);

  return {
    element,
    image,
  };
};

const makeLineGeoJson = (positions = []) => ({
  type: "Feature",
  properties: {},
  geometry: {
    type: "LineString",
    coordinates: toOlaCoordinates(positions),
  },
});

const makeEmptyGeoJson = () => ({
  type: "FeatureCollection",
  features: [],
});

const decodePolyline = (encoded = "") => {
  if (!encoded) return [];

  const points = [];

  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let result = 0;
    let shift = 0;
    let byte;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    const deltaLat = result & 1 ? ~(result >> 1) : result >> 1;

    lat += deltaLat;

    result = 0;
    shift = 0;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    const deltaLng = result & 1 ? ~(result >> 1) : result >> 1;

    lng += deltaLng;

    points.push([lat / 1e5, lng / 1e5]);
  }

  return points;
};

const getCurvedPoints = (start, end, customOffset) => {
  const points = [];
  const steps = 80;
  const midLat = (start[0] + end[0]) / 2;
  const midLng = (start[1] + end[1]) / 2;

  const offset = customOffset || 0.12;

  const cLat = midLat + (end[1] - start[1]) * offset;
  const cLng = midLng - (end[0] - start[0]) * offset;

  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const inv = 1 - t;
    points.push([
      inv * inv * start[0] + 2 * inv * t * cLat + t * t * end[0],
      inv * inv * start[1] + 2 * inv * t * cLng + t * t * end[1],
    ]);
  }
  return points;
};

const getDistanceInKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;

  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const getBearing = (lat1, lng1, lat2, lng2) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const toDeg = (rad) => (rad * 180) / Math.PI;

  const dLng = toRad(lng2 - lng1);

  const y = Math.sin(dLng) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng);

  let brng = toDeg(Math.atan2(y, x));
  return (brng + 360) % 360;
};

export default function OrderTrackingMap({
  pickupLat,
  pickupLng,
  homeLat,
  homeLng,
  driverLat,
  driverLng,

  route = [],
  polyLine = "",

  restaurantIcon,
  homeIcon,
  driverIcon,
  isOrderPickedUp,
  trackingText,
  displayRemainingTime,
}) {
  const { olaMapsApiKey } = useGlobalContext();
  const MAP_MIN = 330;
  const SCREEN_H = typeof window !== "undefined" ? window.innerHeight : 500;
  const MAP_MAX = SCREEN_H * 0.88;
  const [height, setHeight] = useState(MAP_MIN);
  const outerRef = useRef(null);
  const dragInfo = useRef({ dragging: false, startY: 0, startH: MAP_MIN });
  // =====================================================
  // OLA MAP REFS
  // =====================================================

  const olaMapContainerRef = useRef(null);
  const olaMapRef = useRef(null);

  const [olaMapReady, setOlaMapReady] = useState(false);

  // Restaurant marker
  const restaurantMarkerRef = useRef(null);

  // Home / drop marker
  const homeMarkerRef = useRef(null);

  // Driver marker
  const driverMarkerRef = useRef(null);
  const driverMarkerImageRef = useRef(null);
  const driverMarkerIconRef = useRef(null);

  // Driver animation
  const driverCurrentPositionRef = useRef(null);
  const driverAnimationRef = useRef(null);

  const lastFitModeRef = useRef("");

  // =====================================================
  // DRIVER FACING DIRECTION
  // Previous location -> new location
  // =====================================================

  const driverHeadingRef = useRef(0);

  const getDriverFacingRotation = useCallback(
    (fromLat, fromLng, toLat, toLng) => {
      if (!isValidLatLng(fromLat, fromLng) || !isValidLatLng(toLat, toLng)) {
        return driverHeadingRef.current;
      }

      // Driver hasn't moved -> keep previous facing direction
      if (fromLat === toLat && fromLng === toLng) {
        return driverHeadingRef.current;
      }

      const bearing = getBearing(
        Number(fromLat),
        Number(fromLng),
        Number(toLat),
        Number(toLng),
      );

      // Our driver SVG faces sideways by default,
      // therefore +90 aligns the face with movement direction.
       let targetRotation = bearing - 90;

      // const baseRotation = bearing - 90; // current headlight/front alignment
      // let targetRotation = baseRotation + 180; // ab rider/bacche ka head route ki taraf hoga

      // Avoid unnecessary 360 degree spinning.
      const currentRotation = driverHeadingRef.current;

      while (targetRotation - currentRotation > 180) {
        targetRotation -= 360;
      }

      while (targetRotation - currentRotation < -180) {
        targetRotation += 360;
      }

      driverHeadingRef.current = targetRotation;

      return targetRotation;
    },
    [],
  );

  // =====================================================
  // ACTUAL ROUTE FROM DRIVER TRACKER API
  // =====================================================

  const routePositions = useMemo(() => {
    // 1. Pehle API route array use karo
    if (Array.isArray(route) && route.length > 1) {
      const positions = route
        .map((item) => [Number(item?.lat), Number(item?.lng)])
        .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));

      if (positions.length > 1) {
        return positions;
      }
    }

    // 2. route [] ho to encoded polyLine decode karo
    if (polyLine) {
      try {
        return decodePolyline(polyLine);
      } catch (error) {
        console.error("POLYLINE DECODE ERROR:", error);
      }
    }

    return [];
  }, [route, polyLine]);

  console.log("FINAL ROUTE POSITIONS:", routePositions);

  // =====================================================
  // REMAINING DELIVERY ROUTE
  // Driver move karega to completed route remove hota jayega
  // =====================================================

  const remainingRoutePositions = useMemo(() => {
    if (routePositions.length < 2) {
      return [];
    }

    // Driver location abhi nahi mili
    if (!isValidLatLng(driverLat, driverLng)) {
      return routePositions;
    }

    // Pickup se pehle actual road route bilkul nahi dikhana
    if (!isOrderPickedUp) {
      return [];
    }

    let nearestIndex = 0;
    let minimumDistance = Infinity;

    routePositions.forEach((point, index) => {
      const [routeLat, routeLng] = point;

      const distance = getDistanceInKm(
        driverLat,
        driverLng,
        routeLat,
        routeLng,
      );

      if (distance < minimumDistance) {
        minimumDistance = distance;
        nearestIndex = index;
      }
    });

    // Driver ke nearest route point ke baad ka route
    const remainingRoute = routePositions.slice(nearestIndex + 1);

    // Driver current location ko first point rakho
    return [[driverLat, driverLng], ...remainingRoute];
  }, [routePositions, driverLat, driverLng, isOrderPickedUp]);

  console.log("REMAINING ROUTE POSITIONS:", remainingRoutePositions);

  // =====================================================
  // PICKUP → DROP STRAIGHT LINE
  // Sirf order pickup hone se pehle
  // =====================================================

  const pickupToDropCurvedLine = useMemo(() => {
    // Order pickup hone ke baad ye line nahi dikhani
    if (isOrderPickedUp) {
      return [];
    }

    if (
      !isValidLatLng(pickupLat, pickupLng) ||
      !isValidLatLng(homeLat, homeLng)
    ) {
      return [];
    }

    // Pickup → Drop curved route
    return getCurvedPoints([pickupLat, pickupLng], [homeLat, homeLng], -0.22);
  }, [pickupLat, pickupLng, homeLat, homeLng, isOrderPickedUp]);

  const boundPoints = useMemo(() => {
    const points = [];

    // =====================================================
    // BEFORE PICKUP
    // DRIVER + RESTAURANT + DROP tino map me visible rahenge
    // =====================================================

    if (!isOrderPickedUp) {
      // Driver
      if (isValidLatLng(driverLat, driverLng)) {
        points.push([driverLat, driverLng]);
      }

      // Restaurant / Pickup
      if (isValidLatLng(pickupLat, pickupLng)) {
        points.push([pickupLat, pickupLng]);
      }

      // Customer / Drop
      if (isValidLatLng(homeLat, homeLng)) {
        points.push([homeLat, homeLng]);
      }

      return points;
    }

    // =====================================================
    // AFTER PICKUP
    // DRIVER + REMAINING ACTUAL ROUTE + DROP visible rahenge
    // =====================================================

    if (isValidLatLng(driverLat, driverLng)) {
      points.push([driverLat, driverLng]);
    }

    if (remainingRoutePositions.length > 1) {
      points.push(...remainingRoutePositions);
    }

    if (isValidLatLng(homeLat, homeLng)) {
      points.push([homeLat, homeLng]);
    }

    return points;
  }, [
    isOrderPickedUp,
    driverLat,
    driverLng,
    pickupLat,
    pickupLng,
    homeLat,
    homeLng,
    remainingRoutePositions,
  ]);

  const curvedDots = useMemo(() => {
    const pointA = [driverLat, driverLng];

    const pointB = isOrderPickedUp
      ? [homeLat, homeLng]
      : [pickupLat, pickupLng];

    if (
      !isValidLatLng(pointA[0], pointA[1]) ||
      !isValidLatLng(pointB[0], pointB[1])
    ) {
      return [];
    }

    return getCurvedPoints(pointA, pointB, 0.18);
  }, [
    driverLat,
    driverLng,
    pickupLat,
    pickupLng,
    homeLat,
    homeLng,
    isOrderPickedUp,
  ]);

  const onMove = useCallback(
    (y) => {
      if (!dragInfo.current.dragging) return;
      const delta = y - dragInfo.current.startY;
      const nextH = Math.min(
        MAP_MAX,
        Math.max(MAP_MIN, dragInfo.current.startH + delta),
      );
      setHeight(nextH);
      if (outerRef.current) outerRef.current.style.height = `${nextH}px`;
    },
    [MAP_MAX, MAP_MIN],
  );

  useEffect(() => {
    const move = (e) => {
      if (dragInfo.current.dragging) {
        if (e.cancelable) e.preventDefault();
        onMove(e.touches ? e.touches[0].clientY : e.clientY);
      }
    };
    const stop = () => {
      dragInfo.current.dragging = false;
    };

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", stop);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", stop);
    };
  }, [onMove]);

  // =====================================================
  // OLA MAP INITIALIZATION
  // =====================================================

  useEffect(() => {
    let cancelled = false;
    let mapInstance = null;
    let handleLoad = null;

    const initializeOlaMap = async () => {
      try {
        if (!olaMapContainerRef.current) {
          return;
        }

        if (!olaMapsApiKey) {
          console.error("OLA MAPS API KEY NOT FOUND");

          return;
        }

        if (olaMapRef.current) {
          return;
        }

        let initialLat = 28.6139;
        let initialLng = 77.209;

        if (isValidLatLng(pickupLat, pickupLng)) {
          initialLat = pickupLat;
          initialLng = pickupLng;
        } else if (isValidLatLng(homeLat, homeLng)) {
          initialLat = homeLat;
          initialLng = homeLng;
        } else if (isValidLatLng(driverLat, driverLng)) {
          initialLat = driverLat;
          initialLng = driverLng;
        }

        const olaMaps = new OlaMaps({
          apiKey: olaMapsApiKey,
        });
        mapInstance = await olaMaps.init({
          style:
            "https://api.olamaps.io/tiles/vector/v1/styles/default-light-standard/style.json",

          container: olaMapContainerRef.current,

          center: [initialLng, initialLat],                                                       

          zoom: 13,
        });

        if (cancelled) {
          mapInstance?.remove?.();
          return;
        }
                                                             
        olaMapRef.current = mapInstance;

        handleLoad = () => {
          if (cancelled) return;

          console.log("OLA MAP READY");

          setOlaMapReady(true);

          setTimeout(() => {
            mapInstance?.resize?.();
          }, 50);
        };

        // Agar style already load ho chuki hai
        if (mapInstance?.loaded?.()) {
          handleLoad();
        } else {
          mapInstance?.on?.("load", handleLoad);
        }

        console.log("OLA MAP INITIALIZED SUCCESSFULLY");
      } catch (error) {
        console.error("OLA MAP INITIALIZATION ERROR:", error);
      }
    };

    initializeOlaMap();

    return () => {
      cancelled = true;

      if (driverAnimationRef.current) {
        cancelAnimationFrame(driverAnimationRef.current);
        driverAnimationRef.current = null;
      }

      restaurantMarkerRef.current?.remove?.();
      restaurantMarkerRef.current = null;

      homeMarkerRef.current?.remove?.();
      homeMarkerRef.current = null;

      driverMarkerRef.current?.remove?.();
      driverMarkerRef.current = null;

      if (mapInstance && handleLoad) {
        mapInstance.off?.("load", handleLoad);
      }

      if (olaMapRef.current) {
        olaMapRef.current.remove?.();
        olaMapRef.current = null;
      }

      setOlaMapReady(false);
    };
  }, [olaMapsApiKey]);

  // =====================================================
  // RESTAURANT + HOME MARKERS
  // =====================================================

  useEffect(() => {
    if (!olaMapReady || !olaMapRef.current) {
      return;
    }

    const map = olaMapRef.current;

    // OLD RESTAURANT MARKER REMOVE
    restaurantMarkerRef.current?.remove?.();
    restaurantMarkerRef.current = null;

    // OLD HOME MARKER REMOVE
    homeMarkerRef.current?.remove?.();
    homeMarkerRef.current = null;

    // =====================================================
    // RESTAURANT
    // =====================================================

    if (isValidLatLng(pickupLat, pickupLng)) {
      if (restaurantIcon) {
        const { element } = createOlaMarkerElement(restaurantIcon, 45);

        restaurantMarkerRef.current = new OlaMaps.Marker({
          element,
          anchor: "center",
        })
          .setLngLat([Number(pickupLng), Number(pickupLat)])
          .addTo(map);
      } else {
        restaurantMarkerRef.current = new OlaMaps.Marker({
          color: "#22c55e",
          anchor: "bottom",
        })
          .setLngLat([Number(pickupLng), Number(pickupLat)])
          .addTo(map);
      }
    }

    // =====================================================
    // HOME / DROP
    // =====================================================

    if (isValidLatLng(homeLat, homeLng)) {
      if (homeIcon) {
        const { element } = createOlaMarkerElement(homeIcon, 40);

        homeMarkerRef.current = new OlaMaps.Marker({
          element,
          anchor: "center",
        })
          .setLngLat([Number(homeLng), Number(homeLat)])
          .addTo(map);
      } else {
        homeMarkerRef.current = new OlaMaps.Marker({
          color: "#2563eb",
          anchor: "bottom",
        })
          .setLngLat([Number(homeLng), Number(homeLat)])
          .addTo(map);
      }
    }

    return () => {
      restaurantMarkerRef.current?.remove?.();
      restaurantMarkerRef.current = null;

      homeMarkerRef.current?.remove?.();
      homeMarkerRef.current = null;
    };
  }, [
    olaMapReady,
    pickupLat,
    pickupLng,
    homeLat,
    homeLng,
    restaurantIcon,
    homeIcon,
  ]);

  // =====================================================
  // LIVE DRIVER MARKER + SMOOTH MOVEMENT
  // Driver face always follows actual movement direction
  // =====================================================

  useEffect(() => {
    if (
      !olaMapReady ||
      !olaMapRef.current ||
      !isValidLatLng(driverLat, driverLng)
    ) {
      return;
    }

    const map = olaMapRef.current;

    const targetPosition = [Number(driverLat), Number(driverLng)];

    // =====================================================
    // INITIAL ROTATION
    // First location par previous location available nahi hoti.
    // Isliye temporary destination-facing direction use karenge.
    // Second location se actual movement direction use hogi.
    // =====================================================

    const fallbackTargetLat = isOrderPickedUp
      ? Number(homeLat)
      : Number(pickupLat);

    const fallbackTargetLng = isOrderPickedUp
      ? Number(homeLng)
      : Number(pickupLng);

    let initialRotation = driverHeadingRef.current;

    if (isValidLatLng(fallbackTargetLat, fallbackTargetLng)) {
      initialRotation = getDriverFacingRotation(
        targetPosition[0],
        targetPosition[1],
        fallbackTargetLat,
        fallbackTargetLng,
      );
    }

    // =====================================================
    // CREATE DRIVER MARKER FIRST TIME
    // OR RECREATE IF ICON CHANGED
    // =====================================================

    if (
      !driverMarkerRef.current ||
      driverMarkerIconRef.current !== driverIcon
    ) {
      driverMarkerRef.current?.remove?.();

      driverMarkerRef.current = null;
      driverMarkerImageRef.current = null;

      if (driverIcon) {
        const { element, image } = createOlaMarkerElement(
          driverIcon,
          40,
          initialRotation,
        );

        driverMarkerImageRef.current = image;

        driverMarkerRef.current = new OlaMaps.Marker({
          element,
          anchor: "center",
        })
          .setLngLat([targetPosition[1], targetPosition[0]])
          .addTo(map);
      } else {
        driverMarkerRef.current = new OlaMaps.Marker({
          color: "#f97316",
          anchor: "bottom",
        })
          .setLngLat([targetPosition[1], targetPosition[0]])
          .addTo(map);
      }

      driverMarkerIconRef.current = driverIcon;

      driverCurrentPositionRef.current = targetPosition;

      return;
    }

    // =====================================================
    // CURRENT DRIVER POSITION
    // =====================================================

    const currentPosition = driverCurrentPositionRef.current;

    if (!currentPosition) {
      driverCurrentPositionRef.current = targetPosition;

      driverMarkerRef.current?.setLngLat([
        targetPosition[1],
        targetPosition[0],
      ]);

      return;
    }

    // =====================================================
    // SAME LOCATION
    // Driver move nahi hua -> direction bhi change nahi hogi
    // =====================================================

    if (
      currentPosition[0] === targetPosition[0] &&
      currentPosition[1] === targetPosition[1]
    ) {
      const idleTargetLat = isOrderPickedUp
        ? Number(homeLat)
        : Number(pickupLat);

      const idleTargetLng = isOrderPickedUp
        ? Number(homeLng)
        : Number(pickupLng);

      if (
        driverMarkerImageRef.current &&
        isValidLatLng(idleTargetLat, idleTargetLng)
      ) {
        const idleRotation = getDriverFacingRotation(
          Number(currentPosition[0]),
          Number(currentPosition[1]),
          idleTargetLat,
          idleTargetLng,
        );

        driverMarkerImageRef.current.style.transform = `rotate(${idleRotation}deg)`;
      }

      return;
    }

    // =====================================================
    // DRIVER FACE -> DESTINATION
    //
    // BEFORE PICKUP : Restaurant ki taraf
    // AFTER PICKUP  : Home / Delivery location ki taraf
    // =====================================================

    const destinationLat = isOrderPickedUp
      ? Number(homeLat)
      : Number(pickupLat);

    const destinationLng = isOrderPickedUp
      ? Number(homeLng)
      : Number(pickupLng);

    if (
      driverMarkerImageRef.current &&
      isValidLatLng(destinationLat, destinationLng)
    ) {
      const destinationRotation = getDriverFacingRotation(
        Number(targetPosition[0]),
        Number(targetPosition[1]),
        destinationLat,
        destinationLng,
      );

      driverMarkerImageRef.current.style.transform = `rotate(${destinationRotation}deg)`;
    }

    // =====================================================
    // OLD ANIMATION STOP
    // =====================================================

    if (driverAnimationRef.current) {
      cancelAnimationFrame(driverAnimationRef.current);

      driverAnimationRef.current = null;
    }

    const startPosition = [currentPosition[0], currentPosition[1]];

    const animationDuration = 2000;

    const startTime = performance.now();

    // =====================================================
    // SMOOTH DRIVER MOVEMENT
    // =====================================================

    const animateDriver = (currentTime) => {
      const elapsed = currentTime - startTime;

      const progress = Math.min(elapsed / animationDuration, 1);

      const easedProgress =
        progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      const currentLat =
        startPosition[0] +
        (targetPosition[0] - startPosition[0]) * easedProgress;

      const currentLng =
        startPosition[1] +
        (targetPosition[1] - startPosition[1]) * easedProgress;

      driverCurrentPositionRef.current = [currentLat, currentLng];

      driverMarkerRef.current?.setLngLat([currentLng, currentLat]);

      if (progress < 1) {
        driverAnimationRef.current = requestAnimationFrame(animateDriver);
      } else {
        driverCurrentPositionRef.current = targetPosition;

        driverMarkerRef.current?.setLngLat([
          targetPosition[1],
          targetPosition[0],
        ]);
      }
    };

    driverAnimationRef.current = requestAnimationFrame(animateDriver);

    return () => {
      if (driverAnimationRef.current) {
        cancelAnimationFrame(driverAnimationRef.current);

        driverAnimationRef.current = null;
      }
    };
  }, [
    olaMapReady,
    driverLat,
    driverLng,
    driverIcon,
    pickupLat,
    pickupLng,
    homeLat,
    homeLng,
    isOrderPickedUp,
    getDriverFacingRotation,
  ]);

  // =====================================================
  // OLA ROUTE HELPER
  // =====================================================

  const syncOlaLine = useCallback(
    ({
      sourceId,
      layerId,
      positions,
      color,
      width,
      opacity = 1,
      dashArray = null,
    }) => {
      const map = olaMapRef.current;

      if (!map || !olaMapReady) {
        return;
      }

      const validCoordinates = toOlaCoordinates(positions);

      const existingSource = map.getSource?.(sourceId);

      // =====================================================
      // LINE HIDE KARNA
      // =====================================================

      if (validCoordinates.length < 2) {
        if (existingSource?.setData) {
          existingSource.setData(makeEmptyGeoJson());
        }

        return;
      }

      const data = makeLineGeoJson(positions);

      // =====================================================
      // SOURCE ALREADY EXISTS -> UPDATE
      // =====================================================

      if (existingSource?.setData) {
        existingSource.setData(data);
        return;
      }

      // =====================================================
      // FIRST TIME SOURCE CREATE
      // =====================================================

      map.addSource(sourceId, {
        type: "geojson",
        data,
      });

      const paint = {
        "line-color": color,
        "line-width": width,
        "line-opacity": opacity,
      };

      if (dashArray) {
        paint["line-dasharray"] = dashArray;
      }

      map.addLayer({
        id: layerId,
        type: "line",
        source: sourceId,

        layout: {
          "line-join": "round",
          "line-cap": "round",
        },

        paint,
      });
    },
    [olaMapReady],
  );

 // =====================================================
// ROUTE AVAILABILITY
// =====================================================

// Actual route tabhi valid maanenge jab pickup ho chuka ho
// aur driver ke aage minimum 2 valid route points available ho.
const hasActualDeliveryRoute =
  isOrderPickedUp &&
  Array.isArray(remainingRoutePositions) &&
  remainingRoutePositions.length > 1;

// =====================================================
// ALL ROUTES
// =====================================================

useEffect(() => {
  if (!olaMapReady) {
    return;
  }

  // =====================================================
  // 1. DRIVER -> CURRENT TARGET DASHED FALLBACK
  //
  // BEFORE PICKUP:
  // Driver -> Restaurant dashed line
  //
  // AFTER PICKUP:
  // Agar actual route nahi mila to
  // Driver -> Home dashed line continue rahegi
  // =====================================================

  syncOlaLine({
    sourceId: "driver-target-route-source",
    layerId: "driver-target-route-layer",

    positions:
      !isOrderPickedUp || !hasActualDeliveryRoute
        ? curvedDots
        : [],

    color: "#4b5563",
    width: 4,
    opacity: 0.9,

    dashArray: [2.5, 2],
  });

  // =====================================================
  // 2. RESTAURANT -> DROP
  //
  // Sirf pickup se pehle dikhana hai.
  // Pickup hone ke baad driver restaurant se nikal chuka hai,
  // isliye ye line hide ho jayegi.
  // =====================================================

  syncOlaLine({
    sourceId: "pickup-drop-route-source",
    layerId: "pickup-drop-route-layer",

    positions:
      !isOrderPickedUp
        ? pickupToDropCurvedLine
        : [],

    color: "#4b5563",
    width: 4,
    opacity: 0.9,

    dashArray: [2.5, 2],
  });

  // =====================================================
  // 3. ACTUAL ROAD ROUTE
  //
  // Sirf tab show karo jab:
  // - pickup ho chuka ho
  // - valid actual route available ho
  //
  // Route API fail / empty ho to actual route hide rahega
  // aur upar wali dashed fallback automatically visible hogi.
  // =====================================================

  syncOlaLine({
    sourceId: "actual-delivery-route-source",
    layerId: "actual-delivery-route-layer",

    positions:
      hasActualDeliveryRoute
        ? remainingRoutePositions
        : [],

    color: "#3b82f6",
    width: 5,
    opacity: 1,

    dashArray: null,
  });
}, [
  olaMapReady,
  isOrderPickedUp,
  hasActualDeliveryRoute,
  curvedDots,
  pickupToDropCurvedLine,
  remainingRoutePositions,
  syncOlaLine,
]);

  // =====================================================
  // RESET MAP FIT WHEN ORDER LOCATIONS CHANGE
  // =====================================================

  useEffect(() => {
    lastFitModeRef.current = "";
  }, [pickupLat, pickupLng, homeLat, homeLng]);

  // =====================================================
  // FIT ALL RELEVANT LOCATIONS INSIDE MAP
  // Only once before pickup + once after pickup
  // =====================================================

  useEffect(() => {
    if (!olaMapReady || !olaMapRef.current || !Array.isArray(boundPoints)) {
      return;
    }

    const points = boundPoints.filter(([lat, lng]) =>
      isValidLatLng(Number(lat), Number(lng)),
    );

    if (points.length === 0) {
      return;
    }

    // =====================================================
    // CURRENT DELIVERY PHASE
    // =====================================================

    const currentFitMode = isOrderPickedUp ? "AFTER_PICKUP" : "BEFORE_PICKUP";

    // Same phase me driver location update hone par
    // fitBounds dobara mat chalao
    if (lastFitModeRef.current === currentFitMode) {
      return;
    }

    const map = olaMapRef.current;

    // =====================================================
    // SINGLE LOCATION
    // =====================================================

    if (points.length === 1) {
      const [lat, lng] = points[0];

      map.setCenter?.([Number(lng), Number(lat)]);

      map.setZoom?.(15);

      lastFitModeRef.current = currentFitMode;

      console.log("OLA MAP FIT DONE:", currentFitMode);

      return;
    }

    // =====================================================
    // MULTIPLE LOCATIONS
    // =====================================================

    const latitudes = points.map(([lat]) => Number(lat));

    const longitudes = points.map(([, lng]) => Number(lng));

    const minLat = Math.min(...latitudes);

    const maxLat = Math.max(...latitudes);

    const minLng = Math.min(...longitudes);

    const maxLng = Math.max(...longitudes);

    map.fitBounds?.(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      {
        padding: {
          top: 25,
          right: 25,
          bottom: 155,
          left: 25,
        },

        maxZoom: 15.5,

        duration: 0,
      },
    );

    // Mark this phase as fitted
    lastFitModeRef.current = currentFitMode;

    console.log("OLA MAP FIT DONE:", currentFitMode);
  }, [olaMapReady, boundPoints, isOrderPickedUp]);

  // =====================================================
  // MAP RESIZE
  // =====================================================

  useEffect(() => {
    if (!olaMapReady) {
      return;
    }

    const timer = setTimeout(() => {
      olaMapRef.current?.resize?.();
    }, 50);

    return () => clearTimeout(timer);
  }, [height, olaMapReady]);

  return (
    <div
      ref={outerRef}
      style={{
        width: "100%",
        height: MAP_MIN,
        position: "relative",
        overflow: "hidden",
        background: "#f1f5f9",
        touchAction: "none",
      }}
    >
      <div
        style={{ height: height, width: "100%", position: "absolute", top: 0 }}
      >
        <div
          ref={olaMapContainerRef}
          style={{
            width: "100%",
            height: "100%",
            position: "relative",
            touchAction: "auto",
          }}
        />
      </div>
      {/* DRAG HANDLE + TRACKING PANEL */}
      <div
        onMouseDown={(e) => {
          dragInfo.current = {
            dragging: true,
            startY: e.clientY,
            startH: height,
          };
        }}
        onTouchStart={(e) => {
          dragInfo.current = {
            dragging: true,
            startY: e.touches[0].clientY,
            startH: height,
          };
        }}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: "#fff",
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
          padding: "12px 20px 30px",
          boxShadow: "0 -12px 30px rgba(0,0,0,0.12)",
          cursor: "ns-resize",
          touchAction: "none",
        }}
      >
        {/* HANDLE */}
        <div
          style={{
            width: 45,
            height: 5,
            background: "#f1f5f9",
            borderRadius: 10,
            margin: "0 auto 15px",
          }}
        />

        {/* CONTENT */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
          }}
        >
          {/* LEFT SIDE TRACKING TEXT */}
          <div
            style={{
              textAlign: "left",
              flex: 1,
              minWidth: 0,
            }}
          >
            <p
              style={{
                fontSize: "15px",
                color: "#64748b",
                margin: 0,
                fontWeight: "600",
              }}
            >
              {trackingText?.sub}
            </p>

            <h2
              style={{
                fontSize: "14px",
                fontWeight: "700",
                color: "#0f172a",
                margin: "2px 0 0",
              }}
            >
              {trackingText?.main}
            </h2>

            {!Number.isFinite(displayRemainingTime) && (
              <p
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#ea580c",
                  margin: "5px 0 0",
                  lineHeight: "1.3",
                }}
              >
                Assigning delivery partner on priority
              </p>
            )}
          </div>

          {/* REMAINING TIME */}
          {Number.isFinite(displayRemainingTime) && (
            <div
              style={{
                background: "#f97316",
                color: "#fff",
                minWidth: "82px",
                height: "55px",
                padding: "0 10px",
                borderRadius: "12px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontSize: "18px",
                  fontWeight: "800",
                  lineHeight: "1",
                }}
              >
                {Math.ceil(displayRemainingTime)}
              </span>

              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "700",
                  textTransform: "uppercase",
                }}
              >
                mins
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
